import { routeList } from '@/router/helpers/routes'
import {
	isLoggedIn,
	getSessionInfo,
	cancelAllPendingRequests,
	removeRouteParams,
	getRouteParams,
	hideTokenChallengeModal,
	getMobileAppState,
    forceLogout
} from '@/store/helpers/accessControl'
import Store from '@/store/store'

/** Validates our route and if we are logged in to continue
@to {object} - info from the route we are going to
@from {object} - info from the route we are coming from
@next {callback}
@mandatoryRouteParams {Boolean} - prop to trigger params and fallback validation
@fallbackRoute {String} - route to fallback if our route doesnt receive props
@return { void }
*/
export const validateRoute = (to, from, next, mandatoryRouteParams, fallbackRouteName) => {
    if (getSessionInfo() &&
        ((window.location.href.includes('corporate') && !getSessionInfo().isCompany) ||
        (window.location.href.includes('retail') && getSessionInfo().isCompany))) {
        forceLogout()
        return
    }

	if ((from.path === routeList.TRANSFERS_NACIONAL_PATH || from.path === routeList.PAYMENTS_BAI_PAGA_PATH) &&
		window.cordova &&
		window.QRScanner) {
		window.QRScanner.destroy()
	}

	cancelAllPendingRequests()
	hideTokenChallengeModal()

    const applyRevelock = Store.getters['session/getAllCustomerInfo'] && Store.getters['session/getAllCustomerInfo'].afData && Store.getters['session/getAllCustomerInfo'].afData.enabled
	/* Revelock Data */
	const revelockData = {
		customerId: applyRevelock ? Store.getters['session/getAllCustomerInfo'].afData.userId : null,
        sessionToken: applyRevelock ? Store.getters['session/getAllCustomerInfo'].afData.sessionId : null,
		path: to ? to.path : ''
	}

	/* If route is invalid, we kick user to login */
	if (isInvalidRoute(to, from, next)) {
		next({ name: routeList.LOGIN_NAME })
	/* Here we will validate if we have initial actions (MDW or FE) */
	} else if (isLoggedIn() && hasInitialActionToDo() && !initialRoutes().has(to.path)) {
		/* First we check if session needs email/ussd validation */
		if (needsValidateSession()) next(initialActionsRouteMapping.VALIDATE_SESSION)
		/* Second pick company to enter */
		else if (needsToPickACompanyValidation()) next(initialActionsRouteMapping.CHOOSE_DEFAULT_CONTRACT_ACCOUNT_GROUP)
		/* Lastly if mdw sends initial actions */
		else if (mdwInitialAction()) next(initialActionsRouteMapping[getSessionInfo().initialActions[0].type])
	} else {
		if (applyRevelock) storeRevelockData(revelockData)

		if (getMobileAppState() && validateIfThirdMenuEntry(to.fullPath)) {
			next(false)
		}

		/*
		* Since router is removing the capacity of passing params with our route with push
		* we are saving this info in store
		* and then here injected as props
		*/
		if (getRouteParams()) {
			to.params = getRouteParams()
			removeRouteParams()
		}

		if (mandatoryRouteParams && !Object.keys(to.params).length) next({ name: fallbackRouteName })
		else next()
	}
}

/** Validates our route and if we are logged in to continue
@to {object} - info from the route we are going to
@from {object} - info from the route we are coming from
@next {callback}
@fallbackRoute {String} - route to fallback if our route doesnt receive props
@return { void }
*/
export const validateRouteMandatoryProps = (to, from, next, fallbackRoute) => {
	validateRoute(to, from, next, true, fallbackRoute)
}

/* Validate if we are logged in, and trying to move to an authenticated route */
const isInvalidRoute = (to, from, next) => {
	return to.path !== '/' && !isLoggedIn() && (!nonAuthenticatedRoutes().has(to.path) || initialRoutes().has(to.path))
}

/* Checks if we have any initial action to do */
const hasInitialActionToDo = () => {
	if (!isLoggedIn()) return false

	return needsValidateSession() || needsToPickACompanyValidation() || mdwInitialAction()
}

/* Validates session needs email/ussd validation */
const needsValidateSession = () => {
	return getSessionInfo().requiresOutOfBandSessionAuthentication
}

const needsToPickACompanyValidation = (to, from, next) => {
	return getSessionInfo().isCompany && !getSessionInfo().accountGroups.find(obj => obj.userDefaultPreference) && !getSessionInfo().userDefaultPreferencePickedForSession
}

/* Checks if mdw sends initial actions */
const mdwInitialAction = (to, from, next) => {
	const parsedInitialActions = getSessionInfo().initialActions.filter(obj => obj.type !== 'CHOOSE_DEFAULT_CONTRACT_ACCOUNT_GROUP' && initialActionsRouteMapping[obj.type])
	return !!parsedInitialActions.length
}

const initialActionsRouteMapping = {
	VALIDATE_SESSION: routeList.INITIAL_ACTIONS_VALIDATE_SESSION_PATH,
	FIRST_LOGIN: routeList.INITIAL_ACTIONS_FIRST_LOGIN_PATH,
	FIRST_LOGIN_AFTER_REGISTRATION: routeList.INITIAL_ACTIONS_FIRST_LOGIN_PATH,
	CHANGE_PASSWORD_AFTER_RESET: routeList.INITIAL_ACTIONS_FIRST_LOGIN_PATH,
	CHOOSE_DEFAULT_CONTRACT_ACCOUNT_GROUP: routeList.INITIAL_ACTIONS_CHOOSE_DEFAULT_CONTRACT_ACCOUNT_GROUP_PATH,
    INITIAL_ACTIONS_SHOW_SECURE_ASSOCIATION_ENROLLMENT: routeList.INITIAL_ACTIONS_SHOW_SECURE_ASSOCIATION_ENROLLMENT_PATH
}

/* Non authenticated routes */
export const nonAuthenticatedRoutes = () => {
	const nonAuth = new Set()
	nonAuth.add(routeList.NON_AUTHENTICATED_AGENCIES_PATH)
	nonAuth.add(routeList.NON_AUTHENTICATED_EXCHANGES_PATH)
	nonAuth.add(routeList.NON_AUTHENTICATED_CONTACTS_PATH)
	nonAuth.add(routeList.NON_AUTHENTICATED_JOIN_PATH)
	nonAuth.add(routeList.NON_AUTHENTICATED_JOIN_CREATE_ACCOUNT_PATH)
	nonAuth.add(routeList.NON_AUTHENTICATED_JOIN_SELF_ADHESION_PATH)
    nonAuth.add(routeList.NON_AUTHENTICATED_PRE_ACTIVE_MODE_PATH)
    nonAuth.add(routeList.NON_AUTHENTICATED_PRE_ACTIVE_MODE_RECOVER_CREDENTIALS_PATH)
	nonAuth.add(routeList.LOGIN_PATH)
	nonAuth.add(routeList.DESIGN_SHOWROOM_PATH)
	nonAuth.add(routeList.AUTHORIZE_SESSION_PATH)
	nonAuth.add(routeList.JOIN_CREATE_ACCOUNT_PATH)
	nonAuth.add(routeList.RECOVER_CREDENTIALS_PATH)

	return nonAuth
}

/* Non authenticated routes */
export const initialRoutes = () => {
	const initialRoutes = new Set()
	initialRoutes.add(routeList.INITIAL_ACTIONS_VALIDATE_SESSION_PATH)
	initialRoutes.add(routeList.INITIAL_ACTIONS_FIRST_LOGIN_PATH)
	initialRoutes.add(routeList.INITIAL_ACTIONS_CHOOSE_DEFAULT_CONTRACT_ACCOUNT_GROUP_PATH)
	initialRoutes.add(routeList.INITIAL_ACTIONS_SHOW_SECURE_ASSOCIATION_ENROLLMENT_PATH)

	return initialRoutes
}

export const allRoutesEntryAnimationMapping = route => {
	const currentRouteConstKey = Object.keys(routeList).find(key => routeList[key] === route)

	const routeAnimationObj = {
		/* ***********************************************
		*                     Consult                    *
		*********************************************** */
		CONSULT_EXTRACTS_PATH: 'third-level-menu',
		CONSULT_REQUESTS_PATH: 'third-level-menu',
		/* ***********************************************
		*                    Send Money                  *
		*********************************************** */
		SENDMONEY_TRANSFERS_PATH: 'third-level-menu',
		/* ***********************************************
		*                     	Cards                    *
		*********************************************** */
		CARDS_CHARGE_PATH: 'third-level-menu',
		CARDS_PAY_PATH: 'third-level-menu',
		/* ***********************************************
		*                  Bankassurance                 *
		*********************************************** */
		BANKASSURANCE_SUBSCRIBE_PATH: 'third-level-menu',
		/* ***********************************************
		*                    Personalize                 *
		*********************************************** */
		/* Definitions */
		PERSONALIZE_DEFINITIONS_PATH: 'third-level-menu'
	}

	return routeAnimationObj[currentRouteConstKey] ? routeAnimationObj[currentRouteConstKey] : !!routeAnimationObj[currentRouteConstKey]
}

export const validateIfThirdMenuEntry = route => {
	return allRoutesEntryAnimationMapping(route) === 'third-level-menu'
}

export const validateIfUpEntryRoutes = route => {
	const currentRouteConstKey = Object.keys(routeList).find(key => routeList[key] === route)

    const upEntryFeaturesList = [
        'APPROVALS_PENDING_SIGNATURE_PATH',
        'APPROVALS_OPERATORS_CREATE_PATH',
        'APPROVALS_OPERATORS_EDIT_PATH',
        'APPROVALS_OPERATORS_DELETE_PATH',
        'APPROVALS_OPERATORS_BLOCK_PATH',
        'APPROVALS_OPERATORS_UNBLOCK_PATH',
        'COMPANY_PAYMENTS_REQUEST_TPA_BAI_PATH',
        'COMPANY_PAYMENTS_BAI_PAGA_MERCHANT_PATH',
        'ACCOUNTS_EXTRACTS_PATH',
        'ACCOUNTS_INVOICES_PATH',
        'TRANSFERS_NACIONAL_PATH',
        'TRANSFERS_INTERNATIONAL_PATH',
        'TRANSFERS_INSTANT_BAI_PATH',
        'TRANSFERS_KWIK_PATH',
        'TRANSFERS_REQUEST_NAHORA_PATH',
        'TRANSFERS_PERIODIC_EDIT_PATH',
        'TRANSFERS_BENEFICIARIES_CREATE_PATH',
        'TRANSFERS_BENEFICIARIES_EDIT_PATH',
        'PAYMENTS_BATCHFILE_NEW_PAYMENT_PATH',
        'PAYMENTS_SERVICES_FLOW_EMIS_REFERENCE_PATH',
        'PAYMENTS_SERVICES_FLOW_EMIS_SPECIAL_PATH',
        'PAYMENTS_SERVICES_FLOW_EMIS_REFILL_PATH',
        'PAYMENTS_SERVICES_FLOW_EMIS_VOUCHER_PATH',
        'PAYMENTS_SERVICES_FLOW_UNITEL_PATH',
        'PAYMENTS_SERVICES_FLOW_MINFIN_PATH',
        'PAYMENTS_MINFIN_PATH',
        'PAYMENTS_EMIS_REFERENCE_PATH',
        'PAYMENTS_EKWANZA_PATH',
        'PAYMENTS_WALLET_PATH',
        'PAYMENTS_REQUESTS_BAI_PAGA_PAY_PATH',
        'CARDS_CARDLESS_WITHDRAW_GENERATE_CODE_PATH',
        'CARDS_CANCEL_PATH',
        'CARDS_CHANGE_PAYMENT_PATH',
        'CARDS_ANTICIPATE_PAYMENT_PATH',
        'CARDS_BLOCK_PATH',
        'CARDS_LIMITS_PATH',
        'CARDS_TRANSFERS_FLEX_PATH',
        'CARDS_CHARGE_DIRECT_KAMBA_PATH',
        'CARDS_CHARGE_FLEX_PATH',
        'CARDS_CHARGE_VIRTUAL_PATH',
        'CARDS_CHARGE_MULTICAIXA_PATH',
        'CARDS_ACTIVATE_PATH',
        'CARDS_REQUEST_CARD_GENERIC_PATH',
        'CARDS_REQUEST_CARD_CVISA_VIRT_PATH',
        'CARDS_BATCHFILE_NEW_CHARGE_FLEX_PATH',
        'CARDS_BATCHFILE_NEW_CHARGE_KAMBA_PATH',
        'CARDS_BATCHFILE_NEW_CHARGE_MULTICAIXA_PATH',
        'CREDITS_MY_CREDITS_AMORTIZATION_PATH',
        'CREDIT_CREDIT_SOLUTIONS_SUBSCRIBE_PRODUCT_PATH',
        'SAVINGS_MY_SAVINGS_PORTFOLIO_CHARGE_PATH',
        'SAVINGS_MY_SAVINGS_PORTFOLIO_MOBILIZE_PATH',
        'SAVINGS_PRODUCTS_CONSTITUTE_PATH',
        'BANKASSURANCE_SUBSCRIBE_TRAVEL_PATH',
        'BANKASSURANCE_SUBSCRIBE_AUTO_PATH',
        'BANKASSURANCE_SUBSCRIBE_HEALTH_PATH',
        'USER_AREA_PROFILE_PATH',
        'USER_AREA_DEFINITIONS_PASSWORD_CHANGE_PATH',
        'USER_AREA_DEFINITIONS_FRIENDLY_USERNAME_PATH'
    ]

	return upEntryFeaturesList.find(entry => entry === currentRouteConstKey)
}

export const revelockDataHandlerNotLogged = (to) => {
	const revelockData = {
		customerId: null,
		sessionToken: null,
		path: to ? to.path : ''
	}

	storeRevelockData(revelockData)
}

export const storeRevelockData = async userData => {
	if (window.cordova && Store.getters.getMobileAppState && process.env.VUE_APP_ENV !== 'DEV') {
		window.RevelockSdk.setUserId(userData.customerId)
		window.RevelockSdk.setSessionId(userData.sessionToken)
		window.RevelockSdk.setPosition(userData.path)
	}

	window.userIdentifier = userData.customerId
	window.sessionIdentifier = userData.sessionToken
	window.currentLocation = userData.path
}

export const encryptRevelockData = async data => {
	const msgUint8 = new TextEncoder().encode(data) // encode as (utf-8) Uint8Array
	const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8) // hash the message
	const hashArray = Array.from(new Uint8Array(hashBuffer)) // convert buffer to byte array
	const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('') // convert bytes to hex string

	return hashHex
}
